/**
 * Pure decision logic for the boot-splash watchdog's SECOND checkpoint — the
 * "is this a genuine wedge worth an error-level Sentry event?" decision —
 * extracted so the contract is unit-testable without timers, DOM, or Sentry.
 * Mirrors `resolveSplashAction.ts` (checkpoint 1) and the
 * `providers/AuthProvider/resolveAuthAction.ts` pattern.
 *
 * Why this exists: the watchdog used to emit `boot-splash-stuck` (level
 * `error`) the instant the splash was still visible 8s after mount. But the
 * splash legitimately stays up past 8s on a slow-but-HEALTHY boot — a cold
 * OAuth code→token exchange plus the four boot fetches (self, balances,
 * products, countries) on a poor link — and clears a few seconds later. Those
 * slow boots, and backgrounded tabs (where `setTimeout` is throttled and boot
 * work is paused so the elapsed wall-clock is meaningless), produced a stream
 * of false positives (CUSTOMER-UI-3Z: replays showed the app rendered and the
 * user kept using it).
 *
 * The discriminator a genuine wedge has and a slow boot does not: a wedge keeps
 * the splash up INDEFINITELY. So we re-check after a confirmation delay and only
 * report a splash that is STILL up, while the tab is FOREGROUND.
 */
export type StuckReportDecision =
  // Emit the error: the splash is still up, foreground, with no healthy reason.
  | "report"
  // Do not report: the splash cleared during the confirmation window (a slow
  // boot that succeeded) or a Keycloak redirect is in flight (the healthy slow
  // round-trip). Not a wedge.
  | "skip"
  // The tab is hidden, so the elapsed wall-clock is throttled/meaningless.
  // Defer the decision until the tab is foreground again, then re-evaluate —
  // never report on throttled time, but never drop a real wedge either.
  | "defer-until-visible"
  // An OAuth code→token exchange is still in flight and has not errored. That
  // exchange is bounded by OIDC_REQUEST_TIMEOUT_SECONDS, which is LONGER than
  // the confirmation checkpoint, so reporting now alarms inside the window
  // where auth is still operating within its own designed bound. Defer past
  // that bound, then re-evaluate — a genuinely hung exchange still reports.
  | "defer-until-auth-settles";

export interface StuckReportSignals {
  /** Is the boot-splash overlay STILL visible at the confirmation checkpoint? */
  splashVisible: boolean;
  /**
   * Is an automatic Keycloak signinRedirect in flight? Such a splash is the
   * slow-but-healthy auth round-trip (CUSTOMER-UI-6B / CUSTOMER-UI-3Z) that
   * self-heals when the callback lands — not a wedge.
   */
  redirectInFlight: boolean;
  /**
   * Is the tab currently foreground (`document.visibilityState === "visible"`)?
   * A hidden tab throttles timers and pauses boot work, so the 8s+ the watchdog
   * measured did not correspond to 8s of the user actually waiting on a frozen
   * screen.
   */
  tabVisible: boolean;
  /**
   * Is the app still processing an INBOUND Keycloak callback — i.e. the URL
   * carries `?code`+`?state` with no `?error`, so the code→token exchange has
   * not yet settled?
   *
   * Distinct from `redirectInFlight`, which tracks an OUTBOUND redirect this
   * app initiated. Nothing previously covered the inbound leg, so a cold code
   * exchange — the exact "slow but healthy" case this module's header names —
   * fell straight through to `report` (CUSTOMER-UI-3Z, 2026-07-29: events
   * tagged `boot.hasAuthCallbackParams:true` / `boot.oauthError:none` /
   * `boot.signinRedirectInFlight:false`, one of which paired with a
   * `boot-splash-recovered` 58ms later).
   *
   * Callers MUST pass `false` on the deferred re-check so the deferral is
   * one-shot: the wedge is delayed, never dropped.
   */
  authCallbackInFlight: boolean;
}

export function resolveStuckReport({
  splashVisible,
  redirectInFlight,
  tabVisible,
  authCallbackInFlight,
}: StuckReportSignals): StuckReportDecision {
  // Cleared during the confirmation window ⇒ the boot was just slow, not wedged.
  if (!splashVisible) return "skip";
  // Healthy slow auth round-trip — self-heals when the callback lands.
  if (redirectInFlight) return "skip";
  // Throttled background time is not evidence of a wedge — wait for foreground.
  // Checked BEFORE the auth deferral: a hidden tab throttles that timer too, so
  // waiting on it would measure throttled time. The foreground re-check then
  // re-evaluates the callback state.
  if (!tabVisible) return "defer-until-visible";
  // The code exchange is still within its own OIDC timeout budget — wait it out
  // rather than alarming early. Deferral, not suppression.
  if (authCallbackInFlight) return "defer-until-auth-settles";
  return "report";
}
